<script lang="ts">
	/**
	 * One visit, event by event (§12.10).
	 *
	 * This is the whole reason the raw events are kept rather than only their rollups: an aggregate
	 * says a hundred people abandoned, and only a timeline says *this* person selected the annual
	 * price, typed an email, was shown `tax.countryMissing`, and left. That difference is a fix
	 * versus a statistic.
	 *
	 * The visitor's other visits are listed beside it, and the accounts the browser has turned out
	 * to be are a **list** — §12.1's stitch is many-to-many, because a shared machine really is two
	 * people and picking one would be a claim the data does not support.
	 *
	 * A modal rather than a page, because it is always opened *from* a number somebody is reading:
	 * a route would lose the filter and the panel they came from.
	 */

	import { Modal } from 'flowbite-svelte';
	import { EmptyState, ModalHeader, RelativeDate } from '../../../components';
	import { formatAmount } from '../../../stores/storefront';
	import type { AnalyticsSessionDetail, AnalyticsVisitorDetail } from '../../../AppTypes';
	import { getAnalyticsSession, getAnalyticsVisitor } from '../../../AppNetwork';
	import { createEventDispatcher } from 'svelte';

	/** The visit to show, or empty for none. The parent owns this; see `FormModal`'s own note. */
	export let sessionUUID: string = '';

	const dispatcher = createEventDispatcher<{ close: void; open: string }>();

	let detail: AnalyticsSessionDetail | null = null;
	let visitor: AnalyticsVisitorDetail | null = null;
	let error = '';

	/*
	 * A mirror of the parent's prop, for the reason `FormModal` documents: flowbite's `Modal` writes
	 * `open` itself on a dismissal, and a parent driving it from an `{#if}` would be left believing
	 * the dialog was still up — the panel then looks frozen with nothing logged.
	 */
	let dialogOpen = false;
	$: dialogOpen = sessionUUID.length > 0;
	$: if (!dialogOpen && sessionUUID.length > 0) dispatcher('close');

	$: void fnLoad(sessionUUID);

	async function fnLoad(uuid: string): Promise<void> {
		if (uuid.length === 0) {
			detail = null;
			visitor = null;
			return;
		}
		detail = null;
		visitor = null;
		error = '';
		try {
			detail = await getAnalyticsSession(uuid);
			// The visitor read is a **second** guarded call: a timeline must still render when the
			// browser's history cannot be fetched, which is the half of this that grows without
			// bound.
			try {
				visitor = await getAnalyticsVisitor(detail.session.visitorUUID);
			} catch {
				visitor = null;
			}
		} catch (caught) {
			error = caught instanceof Error ? caught.message : 'Failed to load the visit';
		}
	}

	/** The one line under an event that says what it actually was. */
	function fnDetail(event: { path: string | null; productName: string | null; properties: Record<string, unknown> }): string {
		const parts: string[] = [];
		if (event.productName !== null) parts.push(event.productName);
		if (event.path !== null) parts.push(event.path);
		for (const [key, value] of Object.entries(event.properties)) {
			if (value === null || value === undefined) continue;
			parts.push(`${key}: ${String(value)}`);
		}
		return parts.join(' · ');
	}

	/** An `error_shown` is the row somebody opened this timeline to find. */
	function fnTone(type: string): string {
		if (type === 'error_shown') return 'text-red-600 dark:text-red-400';
		if (type === 'checkout_created' || type === 'signin_completed')
			return 'text-green-600 dark:text-green-400';
		return '';
	}
</script>

<Modal bind:open={dialogOpen} size="lg" outsideclose>
	<svelte:fragment slot="header">
		<ModalHeader
			title="One visit"
			faIcon="route"
			subtitle={detail === null ? 'Loading…' : `${detail.events.length} events`}
		/>
	</svelte:fragment>

	{#if error.length > 0}
		<EmptyState variant="error" title="This visit could not be loaded." description={error} />
	{:else if detail === null}
		<EmptyState variant="loading" />
	{:else}
		<div class="space-y-4">
			<dl class="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
				<div>
					<dt class="card-label">Started</dt>
					<dd><RelativeDate date={detail.session.startedAt} /></dd>
				</div>
				<div>
					<dt class="card-label">Who</dt>
					<dd>{detail.session.userEmail ?? 'Not signed in'}</dd>
				</div>
				<div>
					<dt class="card-label">Where</dt>
					<dd>{detail.session.country ?? 'Unknown'}</dd>
				</div>
				<div>
					<dt class="card-label">Device</dt>
					<dd>{detail.session.deviceType ?? 'Unknown'} · {detail.session.browser ?? 'Unknown'}</dd>
				</div>
				<div>
					<dt class="card-label">Came from</dt>
					<dd>{detail.session.utmSource ?? detail.session.referrerHost ?? 'Direct'}</dd>
				</div>
				<div>
					<dt class="card-label">Landed on</dt>
					<dd>{detail.session.entryPath ?? '—'}</dd>
				</div>
			</dl>

			{#if detail.purchases.length > 0}
				<div class="widget bg-depth">
					<div class="card-label mb-1">
						<i class="fa-solid fa-receipt text-[11px] mr-1" />Bought in this visit
					</div>
					{#each detail.purchases as purchase (purchase.uuid)}
						<div class="flex justify-between gap-3 text-sm">
							<span>{purchase.kind === 'order' ? 'One-time purchase' : 'Subscription'}</span>
							<span class="tabular-nums font-medium">
								{$formatAmount(purchase.amount, purchase.currency) ??
									`${purchase.amount} ${purchase.currency}`}
							</span>
						</div>
					{/each}
				</div>
			{/if}

			<div>
				<div class="card-label mb-2"><i class="fa-solid fa-timeline text-[11px] mr-1" />Timeline</div>
				{#if detail.events.length === 0}
					<EmptyState variant="empty" title="No events on this visit." compact />
				{:else}
					<ol class="space-y-2">
						{#each detail.events as event (event.id)}
							<li class="flex gap-3 text-sm">
								<span class="text-default text-xs tabular-nums shrink-0 w-16">
									{new Date(event.receivedAt).toLocaleTimeString(undefined, {
										hour: '2-digit',
										minute: '2-digit',
										second: '2-digit'
									})}
								</span>
								<span class="min-w-0">
									<span class="font-medium {fnTone(event.type)}">{event.type}</span>
									{#if fnDetail(event).length > 0}
										<span class="text-default block text-xs">{fnDetail(event)}</span>
									{/if}
								</span>
							</li>
						{/each}
					</ol>
				{/if}
			</div>

			{#if visitor !== null}
				<div class="border-t border-widget-color pt-3">
					<div class="card-label mb-2">
						<i class="fa-solid fa-user-clock text-[11px] mr-1" />This browser
					</div>
					<p class="text-default text-sm">
						{visitor.sessionCount.toLocaleString()} visits since
						<RelativeDate date={visitor.firstSeenAt} />, first arriving on
						{visitor.landingPath ?? 'an unknown page'} from
						{visitor.utmSource ?? visitor.referrerHost ?? 'a direct visit'}.
					</p>
					{#if visitor.linkedUsers.length > 0}
						<p class="text-default text-sm mt-1">
							Signed in as {visitor.linkedUsers
								.map(user => user.email ?? user.displayName ?? user.userUUID)
								.join(', ')}.
							{#if visitor.linkedUsers.length > 1}
								More than one account has used this browser, which is ordinary on a shared
								machine.
							{/if}
						</p>
					{/if}
					{#if visitor.sessions.length > 1}
						<ul class="mt-2 space-y-1">
							{#each visitor.sessions.filter(s => s.sessionUUID !== detail?.session.sessionUUID).slice(0, 10) as other (other.sessionUUID)}
								<li class="text-sm">
									<button
										type="button"
										class="text-primary-600 dark:text-primary-400 hover:underline"
										on:click={() => dispatcher('open', other.sessionUUID)}
									>
										<RelativeDate date={other.startedAt} />
									</button>
									<span class="text-default">· {other.eventCount} events · {other.entryPath ?? '—'}</span>
								</li>
							{/each}
						</ul>
					{/if}
				</div>
			{/if}
		</div>
	{/if}
</Modal>
